import React from "react";

type CameraTimestamp = { timestamp: number; enabled: boolean };

type UseNoCameraOverlayArgs = {
  cameraTimestamps: CameraTimestamp[];
  currentTime?: number;
  src?: string;
  videoEl: HTMLVideoElement | null;
};

export const useNoCameraOverlay = ({
  cameraTimestamps,
  currentTime,
  src,
  videoEl,
}: UseNoCameraOverlayArgs): boolean => {
  const firstCameraOnTimestamp = React.useMemo(() => {
    if (cameraTimestamps.length === 0) {
      return undefined;
    }

    const sortedTimestamps = [...cameraTimestamps].sort(
      (a, b) => a.timestamp - b.timestamp
    );
    const firstTimestamp = sortedTimestamps[0];
    if (!firstTimestamp.enabled || firstTimestamp.timestamp <= 0) {
      return undefined;
    }
    return firstTimestamp.timestamp;
  }, [cameraTimestamps]);

  const [hasRenderedFrameAfterCameraOn, setHasRenderedFrameAfterCameraOn] =
    React.useState(false);

  React.useEffect(() => {
    setHasRenderedFrameAfterCameraOn(false);
  }, [src, firstCameraOnTimestamp]);

  React.useEffect(() => {
    if (
      firstCameraOnTimestamp === undefined ||
      currentTime === undefined ||
      currentTime < firstCameraOnTimestamp
    ) {
      setHasRenderedFrameAfterCameraOn(false);
      return;
    }
    if (hasRenderedFrameAfterCameraOn || !videoEl) {
      return;
    }

    let cancelled = false;
    const markFrameRendered = (): void => {
      if (!cancelled) {
        setHasRenderedFrameAfterCameraOn(true);
      }
    };

    const videoWithFrameCallback = videoEl as HTMLVideoElement & {
      requestVideoFrameCallback?: (callback: () => void) => number;
    };

    if (videoWithFrameCallback.requestVideoFrameCallback) {
      videoWithFrameCallback.requestVideoFrameCallback(() => {
        markFrameRendered();
      });
      return () => {
        cancelled = true;
      };
    }

    const timeoutId = window.setTimeout(() => {
      if (
        videoEl.readyState >= 2 &&
        videoEl.videoWidth > 0 &&
        videoEl.videoHeight > 0
      ) {
        markFrameRendered();
      }
    }, 50);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    firstCameraOnTimestamp,
    currentTime,
    hasRenderedFrameAfterCameraOn,
    videoEl,
  ]);

  if (firstCameraOnTimestamp === undefined || currentTime === undefined) {
    return false;
  }

  return (
    currentTime < firstCameraOnTimestamp ||
    (currentTime >= firstCameraOnTimestamp && !hasRenderedFrameAfterCameraOn)
  );
};
